/**
 * Client for the token-authenticated web onboarding endpoints
 * (dream-be routes/wa-onboarding-web.ts).
 */

export const WEB_ONBOARDING_STEPS = [
  'privacy',
  'accounts',
  'briefing',
  'integrations',
  'assistant',
  'interests',
  'already-done',
  'referrals',
  'first-task',
] as const;

export type WebOnboardingStep = (typeof WEB_ONBOARDING_STEPS)[number];

export type AccountSlot = 'personal' | 'work';

export type OnboardingInterest =
  | 'calendar'
  | 'todos'
  | 'shop'
  | 'inbox'
  | 'watch'
  | 'event'
  | 'home'
  | 'work';

/** The organisation the user is invited to or already active in. */
export interface OnboardingOrganisation {
  organisationId: string;
  name: string;
  inviterName: string | null;
  status: 'invited' | 'active';
}

export interface FirstTaskSuggestion {
  id: string;
  label: string;
  tagline: string | null;
  evidence: string | null;
}

export interface FirstTasksState {
  status: 'running' | 'ready' | 'failed';
  generation: number;
  startedAt: string;
  completedAt: string | null;
  suggestions: FirstTaskSuggestion[];
  error: string | null;
}

export interface MemoryHighlightItem {
  text: string;
  evidence: string;
  emittedAt: string;
}

export interface MemoryHighlightsState {
  status: 'running' | 'ready' | 'failed';
  generation: number;
  startedAt: string;
  completedAt: string | null;
  items: MemoryHighlightItem[];
  error: string | null;
}

export type PreviewDraftKind = 'follow_up' | 'reply';

export interface PreviewDraftItem {
  kind: PreviewDraftKind;
  subject: string;
  counterpart: string;
  counterpartEmail: string | null;
  waitingSince: string | null;
  waitingDays: number | null;
  snippet: string;
  gmailMessageId: string;
  gmailThreadId: string;
  link: string;
  accountEmail: string | null;
}

export interface AnnouncedCandidate {
  kind: PreviewDraftKind;
  counterpart: string;
}

export interface PreviewDraftsState {
  status: 'running' | 'ready' | 'failed';
  generation: number;
  startedAt: string;
  completedAt: string | null;
  followUps: PreviewDraftItem[];
  replies: PreviewDraftItem[];
  announcedCandidates?: AnnouncedCandidate[];
  error: string | null;
}

export interface WebOnboardingState {
  version: 1;
  currentStep: WebOnboardingStep;
  accountSlots: Record<string, AccountSlot>;
  workConnectAttemptedAt: string | null;
  briefingTime: string;
  eveningRoundUp: boolean;
  trackOwedReplies: boolean;
  /** Null until the user touches the switch; each inbox then keeps its own
   * default (see `ConnectedAccount.emailLabelling`). */
  inboxLabelling: boolean | null;
  emailPermission: 'express';
  spendPermission: 'express';
  firstTask: string | null;
  /** What the user asked for help with on the picks screen. */
  picks?: OnboardingInterest[] | null;
  previewDrafts: PreviewDraftsState | null;
  firstTasks: FirstTasksState | null;
  memoryHighlights: MemoryHighlightsState | null;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface ConnectedAccount {
  id: number;
  identifier: string | null;
  type: 'GOOGLE' | 'MICROSOFT' | string;
  slot: AccountSlot | null;
  emailLabelling: boolean;
  /** Capability categories (e.g. 'Email', 'Calendar') Dream cannot use on this account. */
  missingCapabilities?: string[];
  /** The provider serves no mailbox for this account (Google account without Gmail). */
  mailboxUnavailable?: boolean;
  /** Mail read + compose granted, so the preview drafts can be generated from it. */
  canDraftEmail?: boolean;
}

export interface ConnectedIntegration {
  key: string;
  identifier: string | null;
}

export interface SpottedIntegration {
  key: string;
  messageCount: number;
  lastSeen: string | null;
}

export interface WizardPayload {
  state: WebOnboardingState;
  language?: string | null;
  userName: string;
  assistantName: string;
  assistantFullName?: string;
  assistantEmail?: string;
  accountConnected: boolean;
  /** "Help make your assistant better" — true unless the user switched it off. */
  analyticsConsent?: boolean;
  whatsappNumber: string;
  returnToWhatsAppUrl: string;
  referralLink?: string | null;
  monthlyCredits?: number;
  monthlyCreditsValue?: number;
  currency?: 'GBP' | 'USD';
  referralRewardCredits?: number;
  referralRewardValue?: number;
  /** 'v3' = free month / single plan wording (personal billing v3). */
  billingModel?: 'credits' | 'v3';
  accounts: ConnectedAccount[];
  integrations?: ConnectedIntegration[];
  spottedIntegrations?: SpottedIntegration[];
  organisation?: OnboardingOrganisation | null;
}

export interface StatePatch {
  currentStep?: WebOnboardingStep;
  workConnectAttempted?: boolean;
  briefingTime?: string;
  eveningRoundUp?: boolean;
  trackOwedReplies?: boolean;
  inboxLabelling?: boolean;
  analyticsConsent?: boolean;
  picks?: OnboardingInterest[];
  /** Re-file a connected account as work or personal. */
  accountSlot?: { oauthId: number; slot: AccountSlot };
}

export const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/** Error from a wizard endpoint that reports a machine-readable reason. */
export class WizardApiError extends Error {
  code: string | null;
  retryAfterSeconds: number | null;
  attemptsRemaining: number | null;

  constructor(
    message: string,
    details: {
      code?: unknown;
      retryAfterSeconds?: unknown;
      attemptsRemaining?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'WizardApiError';
    this.code = typeof details.code === 'string' ? details.code : null;
    this.retryAfterSeconds =
      typeof details.retryAfterSeconds === 'number'
        ? details.retryAfterSeconds
        : null;
    this.attemptsRemaining =
      typeof details.attemptsRemaining === 'number'
        ? details.attemptsRemaining
        : null;
  }
}

async function parseResponse(response: Response): Promise<WizardPayload> {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new WizardApiError(
      payload?.error || payload?.message || 'Something went wrong. Try again.',
      payload ?? {},
    );
  }
  return payload?.data as WizardPayload;
}

export async function fetchWizardState(token: string): Promise<WizardPayload> {
  const response = await fetch(
    `${apiBase}/wa-onboarding/web/state?token=${encodeURIComponent(token)}`,
  );
  return parseResponse(response);
}

export async function patchWizardState(
  token: string,
  patch: StatePatch,
): Promise<WizardPayload> {
  const response = await fetch(`${apiBase}/wa-onboarding/web/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...patch }),
  });
  return parseResponse(response);
}

export async function saveAssistantName(
  token: string,
  name: string,
): Promise<WizardPayload> {
  const response = await fetch(`${apiBase}/wa-onboarding/web/assistant-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, name }),
  });
  return parseResponse(response);
}

export async function completeWizard(
  token: string,
  firstTask: string,
): Promise<WizardPayload> {
  const response = await fetch(`${apiBase}/wa-onboarding/web/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, firstTask }),
  });
  return parseResponse(response);
}

export async function disconnectAccount(
  token: string,
  oauthId: number,
): Promise<void> {
  const response = await fetch(`${apiBase}/wa-onboarding/accounts/disconnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, oauthId }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(
      payload?.error || payload?.message || 'Unable to disconnect account.',
    );
  }
}

export function connectUrl(
  token: string,
  provider: 'google' | 'outlook',
  slot: AccountSlot | 'auto',
): string {
  const route =
    provider === 'google'
      ? '/wa-onboarding/connect'
      : '/wa-onboarding/connect-microsoft';
  return `${apiBase}${route}?token=${encodeURIComponent(token)}&slot=${slot}`;
}

export function onboardingAssetUrl(file: string): string {
  return `${apiBase}/wa-onboarding/assets/${file}`;
}

/** OAuth apps connect via a redirect through the backend initiate route. */
export function integrationConnectUrl(token: string, app: string): string {
  const params = new URLSearchParams({ token, app });
  return `${apiBase}/wa-onboarding/web/integrations/connect?${params}`;
}

export async function connectApiKeyIntegration(
  token: string,
  app: 'devin' | 'lusha',
  apiKey: string,
): Promise<{ success?: boolean; email?: string }> {
  const response = await fetch(
    `${apiBase}/wa-onboarding/web/integrations/api-key`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, app, apiKey }),
    },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      payload?.error || payload?.message || 'Could not connect. Try again.',
    );
  }
  return payload as { success?: boolean; email?: string };
}
